import React, { useState, useEffect } from 'react';

import { Modal, Input, Text, FormAlert } from '@ui-library';

import api from '../../../utils/axios';

import type { IUpdateProfileDialogProps } from './UpdateProfileDialog.type';

export const UpdateProfileDialog: React.FC<IUpdateProfileDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userEmail,
  userPassword,
  actionData
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && !profileLoaded) {
      // Reset state when popup opens and load profile directly
      setFirstName('');
      setLastName('');
      setProfileLoaded(false);
      setError('');
      loadProfileData();
    }
  }, [isOpen, profileLoaded]);

  const loadProfileData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post(actionData?.profile_endpoint?.replace('/api', '') || '/auth/get-profile-for-update', {
        email: userEmail,
        password: userPassword
      });

      if (response.data.success && response.data.profile) {
        setFirstName(response.data.profile.firstName || '');
        setLastName(response.data.profile.lastName || '');
        setProfileLoaded(true);
      }
    } catch (err: any) {
      let errorMessage = 'Failed to load profile data. Please try again.';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data) {
        errorMessage = typeof err.response.data === 'string' ? err.response.data : errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateProfileStep = () => {
    if (!firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    return true;
  };

  const handleProfileSubmit = async () => {
    if (!validateProfileStep()) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post(actionData?.update_endpoint?.replace('/api', '') || '/auth/update-profile-action', {
        email: userEmail,
        password: userPassword,
        firstName: firstName.trim(),
        lastName: lastName.trim()
      });
      
      if (response.data.success) {
        onSuccess();
        // Reset form
        setFirstName('');
        setLastName('');
        setProfileLoaded(false);
      }
    } catch (err: any) {
      let errorMessage = 'Failed to update profile. Please try again.';
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFirstName('');
      setLastName('');
      setProfileLoaded(false);
      setError('');
      onClose?.();
    }
  };

  const clearError = () => {
    if (error) setError('');
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Update Profile Required"
      size="md"
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleProfileSubmit}
      submitLabel={loading ? 'Updating...' : 'Update Profile'}
      cancelLabel="Cancel"
      submitDisabled={loading}
      submitLoading={loading}
    >
      <div className="flex flex-col gap-bg-v2-lg">
        {loading && !profileLoaded ? (
          // Loading state while fetching profile data
          <div className="text-center py-bg-v2-4xl">
            <div className="flex justify-center mb-bg-v2-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-border-brand"></div>
            </div>
            <Text variant="text-sm" color="secondary" as="p">
              Loading your profile...
            </Text>
          </div>
        ) : (
          <>
            <Text variant="text-sm" color="secondary" as="p">
              Please complete your profile information below.
            </Text>

            <FormAlert
              message={error}
              variant="error"
              onDismiss={clearError}
            />

            {/* First Name */}
            <Input
              id="firstName"
              name="firstName"
              type="text"
              label="First Name"
              placeholder="Enter first name"
              required
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                clearError();
              }}
              disabled={loading}
            />

            {/* Last Name */}
            <Input
              id="lastName"
              name="lastName"
              type="text"
              label="Last Name"
              placeholder="Enter last name"
              required
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                clearError();
              }}
              disabled={loading}
            />
          </>
        )}
      </div>
    </Modal>
  );
};
